import { useEffect, useState } from "react";
import { UserCog, Plus, Trash2, KeyRound, Mail } from "lucide-react";
import { PermissionPicker } from "@/components/admins/PermissionPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Label } from "@/components/ui/label";

function ChangePasswordForm() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (nw.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
    if (nw !== nw2) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      await api.changePassword(cur, nw);
      toast.success("Password aggiornata");
      setCur(""); setNw(""); setNw2("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-t border-border pt-4 space-y-2" data-testid="change-password-form">
      <Label className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> Cambia la tua password</Label>
      <Input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Password attuale (vuota se accedi solo con Google)" />
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="cp-new" type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="Nuova password" />
        <Input data-testid="cp-new2" type="password" value={nw2} onChange={(e) => setNw2(e.target.value)} placeholder="Conferma" />
      </div>
      <Button data-testid="cp-submit" onClick={submit} disabled={busy} variant="secondary" className="w-full gap-2"><KeyRound className="h-4 w-4" /> Aggiorna password</Button>
    </div>
  );
}

const fmtDate = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function InviteStatus({ a }) {
  if (!a.must_set_password) return <span>Attivo{a.activated_at ? ` dal ${fmtDate(a.activated_at)}` : ""}</span>;
  if (!a.invited_at) return <span>In attesa di attivazione · nessun invito inviato</span>;
  if (a.invite_expired) return <span className="text-red-400 font-semibold" data-testid={`admin-invite-expired-${a.user_id}`}>Invito scaduto il {fmtDate(a.invite_expires_at)} · reinviare</span>;
  return <span>In attesa di attivazione · invito valido fino al {fmtDate(a.invite_expires_at)}</span>;
}

function AdminRow({ a, me, onRole, onRemove, perms, onPerms, onInvite }) {
  const isMe = a.user_id === me?.user_id;
  return (
    <div data-testid={`admin-row-${a.user_id}`} className={`rounded-lg px-3 py-2 text-sm space-y-2 ${a.invite_expired ? "bg-red-500/10 border border-red-500/30" : "bg-secondary/40"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold truncate">{a.email} {isMe && <span className="text-xs text-primary">(tu)</span>}</div>
          <div className="text-xs text-muted-foreground" data-testid={`admin-status-${a.user_id}`}>
            <InviteStatus a={a} />
          </div>
        </div>
        <div className="flex items-center gap-1">
          {a.must_set_password && <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => onInvite(a)} data-testid={`admin-invite-${a.user_id}`}><Mail className="h-3.5 w-3.5" /> Reinvia invito</Button>}
          <select data-testid={`admin-role-${a.user_id}`} value={a.role || "admin"} disabled={isMe} onChange={(e) => onRole(a, e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs">
            <option value="admin">Amministratore</option>
            <option value="operator">Operatore</option>
          </select>
          {!isMe && <Button size="icon" variant="ghost" onClick={() => onRemove(a)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>
      {a.role === "operator" && <PermissionPicker all={perms} value={a.permissions || []} onChange={(v) => onPerms(a, v)} idPrefix={`admin-${a.user_id}`} />}
    </div>
  );
}

export default function AdminsDialog({ trigger, defaultOpen = false, onClosed }) {
  const { user } = useAuth();
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (v) => { setOpenState(v); if (!v) onClosed?.(); };
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [pw, setPw] = useState("");
  const [newPerms, setNewPerms] = useState(["stats"]);
  const [perms, setPerms] = useState([]);
  const load = () => api.listAdmins().then(setAdmins);
  useEffect(() => {
    if (open) { load(); api.listPermissions().then(setPerms).catch(() => {}); }
  }, [open]);
  const add = async () => {
    if (pw && pw.length < 8) return toast.error("La password deve avere almeno 8 caratteri");
    try {
      await api.addAdmin(email.trim(), null, role, pw || null, role === "operator" ? newPerms : null);
      toast.success(pw ? "Utente creato: può accedere subito con la password impostata" : "Utente creato: email di invito inviata per impostare la password");
      setEmail(""); setPw("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  const run = (fn) => fn().then(load).catch((e) => toast.error(e?.response?.data?.detail || "Errore"));
  const changeRole = (a, r) => run(() => api.setAdminRole(a.user_id, r));
  const changePerms = (a, v) => run(() => api.setAdminPermissions(a.user_id, v));
  const invite = (a) => api.resendInvite(a.user_id).then(() => { toast.success(`Invito inviato a ${a.email}`); load(); }).catch((e) => toast.error(e?.response?.data?.detail || "Invio non riuscito"));
  const remove = (a) => window.confirm(`Rimuovere ${a.email}?`) && run(() => api.removeAdmin(a.user_id));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button data-testid="open-admins-button" variant="ghost" className="gap-2"><UserCog className="h-4 w-4" /> Admin</Button>}
      </DialogTrigger>
      <DialogContent className="bg-card border-border" data-testid="admins-dialog">
        <DialogHeader><DialogTitle className="font-display">Utenti autorizzati</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Amministratore: accesso completo. Operatore: solo le sezioni che gli assegni qui sotto.</p>
        <div className="flex gap-2">
          <Input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@azienda.it" />
          <select data-testid="admin-role-select" value={role} onChange={(e) => setRole(e.target.value)} className="h-10 rounded-md border border-border bg-background px-2 text-sm">
            <option value="admin">Amministratore</option>
            <option value="operator">Operatore</option>
          </select>
          <Button data-testid="add-admin-button" onClick={add} className="gap-1.5"><Plus className="h-4 w-4" /> Aggiungi</Button>
        </div>
        <Input data-testid="admin-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password iniziale (opzionale, min 8): se vuota invio email di invito" />
        {role === "operator" && (
          <div className="space-y-1">
            <p className="text-[11px] text-muted-foreground">Sezioni consentite all'operatore:</p>
            <PermissionPicker all={perms} value={newPerms} onChange={setNewPerms} idPrefix="new-admin" />
          </div>
        )}
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {admins.map((a) => <AdminRow key={a.user_id} a={a} me={user} onRole={changeRole} onRemove={remove} perms={perms} onPerms={changePerms} onInvite={invite} />)}
        </div>
        <ChangePasswordForm />
      </DialogContent>
    </Dialog>
  );
}
